import { useEffect, useCallback, useRef } from 'react';

const STORAGE_KEY_PREFIX = 'cisa_architecture_';
const DEBOUNCE_DELAY = 500;

/**
 * Hook for persisting architecture diagram state to localStorage
 *
 * @param {string} departmentId - Department ID for scoping storage
 * @param {Function} getState - Function to get current state from useArchitectureState
 * @param {Function} loadState - Function to load state into useArchitectureState
 * @returns {Object} Persistence utilities
 */
export function useArchitecturePersistence(departmentId, getState, loadState) {
  const debounceRef = useRef(null);
  const hasLoadedRef = useRef(false);

  /**
   * Get the storage key for this department
   */
  const getStorageKey = useCallback(() => {
    return `${STORAGE_KEY_PREFIX}${departmentId || 'global'}`;
  }, [departmentId]);

  /**
   * Save current state to localStorage
   */
  const save = useCallback(() => {
    try {
      const state = getState();
      const key = getStorageKey();
      localStorage.setItem(key, JSON.stringify(state));
    } catch (err) {
      console.warn('Failed to save architecture diagram state:', err);
    }
  }, [getState, getStorageKey]);

  /**
   * Debounced save - prevents too many writes during drag operations
   */
  const debouncedSave = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      save();
      debounceRef.current = null;
    }, DEBOUNCE_DELAY);
  }, [save]);

  /**
   * Load state from localStorage
   */
  const load = useCallback(() => {
    try {
      const key = getStorageKey();
      const stored = localStorage.getItem(key);
      if (stored) {
        const state = JSON.parse(stored);
        loadState(state);
        return true;
      }
    } catch (err) {
      console.warn('Failed to load architecture diagram state:', err);
    }
    return false;
  }, [getStorageKey, loadState]);

  /**
   * Clear stored state
   */
  const clear = useCallback(() => {
    try {
      const key = getStorageKey();
      localStorage.removeItem(key);
    } catch (err) {
      console.warn('Failed to clear architecture diagram state:', err);
    }
  }, [getStorageKey]);

  /**
   * Check if there is stored state
   */
  const hasStoredState = useCallback(() => {
    try {
      const key = getStorageKey();
      return localStorage.getItem(key) !== null;
    } catch {
      return false;
    }
  }, [getStorageKey]);

  /**
   * Load state on mount (once per department change)
   */
  useEffect(() => {
    if (!hasLoadedRef.current) {
      load();
      hasLoadedRef.current = true;
    }
  }, [load]);

  /**
   * Reset loaded flag when department changes
   */
  useEffect(() => {
    hasLoadedRef.current = false;
    load();
    hasLoadedRef.current = true;
  }, [departmentId, load]);

  /**
   * Cleanup debounce on unmount
   */
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  return {
    save,
    debouncedSave,
    load,
    clear,
    hasStoredState
  };
}

export default useArchitecturePersistence;
